import { ComposedChart, Line, Bar, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, CartesianGrid, Legend, Cell } from "recharts";

const GRID = "#1E2738";
const AX = { stroke: "#64748B", fontSize: 10, fontFamily: "monospace" };
const fmtDate = (ms) => new Date(ms).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
const fmtDay = (ms) => new Date(ms).toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit" });
const tickFor = (series, key) => (series?.length > 1 && series[series.length - 1][key] - series[0][key] < 2 * 86400000 ? (ms) => new Date(ms).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" }) : fmtDay);
const usd = (v, d = 2) => Number(v || 0).toLocaleString("tr-TR", { minimumFractionDigits: d, maximumFractionDigits: d });
const TT = (lf = fmtDate) => ({ contentStyle: { background: "#121724", border: "1px solid #2A364F", borderRadius: 6, fontSize: 11, fontFamily: "monospace" }, labelFormatter: lf });

export function Card({ title, sub, testid, children, h = "h-64" }) {
  return (
    <section data-testid={testid} className="bg-[#121724] border border-[#1E2738] rounded-lg p-4">
      <div className="text-sm font-bold">{title}</div>
      {sub && <div className="text-[11px] text-[#64748B] mb-2">{sub}</div>}
      <div className={h}>{children}</div>
    </section>
  );
}

export const Empty = ({ text = "Bu aralıkta kapanmış işlem yok." }) => <div className="h-full flex items-center justify-center text-xs text-[#64748B]">{text}</div>;

export function MarginChart({ series, bots }) {
  if (!series?.length) return <Empty text="Teminat zaman serisi 5 dk'da bir kaydedilir; ilk nokta kısa süre sonra görünür." />;
  const keys = Object.keys(bots || {});
  return (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={series} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <defs><linearGradient id="mrg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#F59E0B" stopOpacity={0.3} /><stop offset="100%" stopColor="#F59E0B" stopOpacity={0} /></linearGradient></defs>
        <CartesianGrid stroke={GRID} strokeDasharray="3 3" />
        <XAxis dataKey="ts" tickFormatter={tickFor(series, "ts")} tick={AX} minTickGap={50} />
        <YAxis tick={AX} width={64} tickFormatter={(v) => usd(v, 0)} />
        <Tooltip {...TT()} formatter={(v, n, p) => [`${usd(v)} USDT${n !== "total" ? ` · ${p?.payload?.[`${n}_n`] ?? 0} pozisyon` : ""}`, n === "total" ? "Toplam" : bots[n]?.name || n]} />
        <Area type="stepAfter" dataKey="total" stroke="#F59E0B" fill="url(#mrg)" strokeWidth={1.8} dot={false} isAnimationActive={false} />
        {keys.map((k) => <Line key={k} type="stepAfter" dataKey={k} stroke={bots[k].color} strokeWidth={1.2} dot={false} isAnimationActive={false} />)}
        <Legend wrapperStyle={{ fontSize: 10, fontFamily: "monospace" }} formatter={(v) => (v === "total" ? "Toplam (üç bot)" : bots[v]?.name || v)} />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

export function EquityChart({ series, b0, color }) {
  if (!series?.length) return <Empty text="Ana para zaman serisi 5 dk'da bir kaydedilir; ilk nokta kısa süre sonra görünür." />;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={series} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <defs><linearGradient id="eqg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={color} stopOpacity={0.35} /><stop offset="100%" stopColor={color} stopOpacity={0} /></linearGradient></defs>
        <CartesianGrid stroke={GRID} strokeDasharray="3 3" />
        <XAxis dataKey="ts" tickFormatter={tickFor(series, "ts")} tick={AX} minTickGap={50} />
        <YAxis domain={["auto", "auto"]} tick={AX} width={64} tickFormatter={(v) => usd(v, 0)} />
        <Tooltip {...TT()} formatter={(v, n) => [`${usd(v)} USDT`, { equity: "Ana para (açık K/Z dahil)", realized_eq: "Gerçekleşmiş ana para" }[n] || n]} />
        <ReferenceLine y={b0} stroke="#64748B" strokeDasharray="4 4" label={{ value: `B0 ${usd(b0, 0)}`, fill: "#64748B", fontSize: 10, position: "insideBottomRight" }} />
        <Area type="monotone" dataKey="equity" stroke={color} fill="url(#eqg)" strokeWidth={1.5} dot={false} isAnimationActive={false} />
        <Line type="stepAfter" dataKey="realized_eq" stroke="#F1F5F9" strokeWidth={1} strokeDasharray="3 2" dot={false} isAnimationActive={false} />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

export function ProfitCurveChart({ curve }) {
  if (!curve?.length) return <Empty />;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={curve} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid stroke={GRID} strokeDasharray="3 3" />
        <XAxis dataKey="t" tickFormatter={tickFor(curve, "t")} tick={AX} minTickGap={50} />
        <YAxis yAxisId="l" tick={AX} width={64} tickFormatter={(v) => usd(v, 0)} />
        <YAxis yAxisId="r" orientation="right" tick={AX} width={56} tickFormatter={(v) => usd(v, 0)} />
        <Tooltip {...TT()} formatter={(v, n, p) => [`${usd(v)} USDT`, { cum_net: "Birikimli net kâr", drawdown: "Zirveden düşüş", net: `İşlem (${p?.payload?.symbol} ${p?.payload?.side})` }[n] || n]} />
        <ReferenceLine yAxisId="l" y={0} stroke="#64748B" />
        <Area yAxisId="r" type="monotone" dataKey="drawdown" stroke="#EF4444" fill="#EF4444" fillOpacity={0.12} strokeWidth={1} dot={false} isAnimationActive={false} />
        <Line yAxisId="l" type="stepAfter" dataKey="cum_net" stroke="#10B981" strokeWidth={1.6} dot={false} isAnimationActive={false} />
        <Legend wrapperStyle={{ fontSize: 10, fontFamily: "monospace" }} formatter={(v) => ({ cum_net: "Birikimli net kâr (sol)", drawdown: "Zirveden düşüş (sağ)" }[v] || v)} />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

export function DailyWinLossChart({ daily }) {
  if (!daily?.length) return <Empty />;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={daily} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid stroke={GRID} strokeDasharray="3 3" />
        <XAxis dataKey="t" tickFormatter={fmtDay} tick={AX} minTickGap={30} />
        <YAxis tick={AX} width={40} allowDecimals={false} />
        <Tooltip {...TT(fmtDay)} formatter={(v, n) => [v, { wins: "Kârlı kapanan", losses: "Zararlı kapanan" }[n] || n]} />
        <Bar dataKey="wins" stackId="a" fill="#10B981" isAnimationActive={false} />
        <Bar dataKey="losses" stackId="a" fill="#EF4444" isAnimationActive={false} radius={[3, 3, 0, 0]} />
        <Legend wrapperStyle={{ fontSize: 10, fontFamily: "monospace" }} formatter={(v) => ({ wins: "Kârlı", losses: "Zararlı" }[v] || v)} />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

export function DailyNetChart({ daily }) {
  if (!daily?.length) return <Empty />;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={daily} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid stroke={GRID} strokeDasharray="3 3" />
        <XAxis dataKey="t" tickFormatter={fmtDay} tick={AX} minTickGap={30} />
        <YAxis yAxisId="l" tick={AX} width={56} tickFormatter={(v) => usd(v, 0)} />
        <YAxis yAxisId="r" orientation="right" tick={AX} width={56} tickFormatter={(v) => usd(v, 0)} />
        <Tooltip {...TT(fmtDay)} formatter={(v, n) => [`${usd(v)} USDT`, { net: "Günlük net kâr/zarar", cum_net: "Birikimli net", commission: "Komisyon" }[n] || n]} />
        <ReferenceLine yAxisId="l" y={0} stroke="#64748B" />
        <Bar yAxisId="l" dataKey="net" fill="#10B981" isAnimationActive={false} radius={[3, 3, 0, 0]}>{daily.map((d) => <Cell key={d.day} fill={d.net >= 0 ? "#10B981" : "#EF4444"} />)}</Bar>
        <Line yAxisId="r" type="monotone" dataKey="cum_net" stroke="#F59E0B" strokeWidth={1.4} dot={false} isAnimationActive={false} />
        <Legend wrapperStyle={{ fontSize: 10, fontFamily: "monospace" }} formatter={(v) => ({ net: "Günlük net (sol)", cum_net: "Birikimli net (sağ)" }[v] || v)} />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

export function HourlyChart({ hourly }) {
  if (!hourly?.some((h) => h.wins || h.losses)) return <Empty />;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={hourly} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid stroke={GRID} strokeDasharray="3 3" />
        <XAxis dataKey="hour" tick={AX} tickFormatter={(h) => `${String(h).padStart(2, "0")}`} interval={1} />
        <YAxis yAxisId="l" tick={AX} width={36} allowDecimals={false} />
        <YAxis yAxisId="r" orientation="right" tick={AX} width={56} tickFormatter={(v) => usd(v, 0)} />
        <Tooltip {...TT((h) => `Saat ${String(h).padStart(2, "0")}:00 (İstanbul)`)} formatter={(v, n) => [n === "net" ? `${usd(v)} USDT` : v, { wins: "Kârlı", losses: "Zararlı", net: "Net" }[n] || n]} />
        <Bar yAxisId="l" dataKey="wins" stackId="a" fill="#10B981" isAnimationActive={false} />
        <Bar yAxisId="l" dataKey="losses" stackId="a" fill="#EF4444" isAnimationActive={false} radius={[3, 3, 0, 0]} />
        <Line yAxisId="r" type="monotone" dataKey="net" stroke="#F59E0B" strokeWidth={1.3} dot={false} isAnimationActive={false} />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

export function PnlHistChart({ hist }) {
  if (!hist?.length) return <Empty />;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={hist} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid stroke={GRID} strokeDasharray="3 3" />
        <XAxis dataKey="bucket" tick={AX} tickFormatter={(v) => usd(v, 1)} minTickGap={20} />
        <YAxis tick={AX} width={36} allowDecimals={false} />
        <Tooltip {...TT((v) => `≈ ${usd(v, 1)} USDT`)} formatter={(v) => [v, "İşlem sayısı"]} />
        <Bar dataKey="count" isAnimationActive={false} radius={[3, 3, 0, 0]}>{hist.map((d) => <Cell key={d.bucket} fill={d.bucket >= 0 ? "#10B981" : "#EF4444"} />)}</Bar>
      </ComposedChart>
    </ResponsiveContainer>
  );
}

export function SymbolBars({ rows, testid }) {
  if (!rows?.length) return <Empty />;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={rows} layout="vertical" margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
        <CartesianGrid stroke={GRID} strokeDasharray="3 3" horizontal={false} />
        <XAxis type="number" tick={AX} tickFormatter={(v) => usd(v, 0)} />
        <YAxis type="category" dataKey="symbol" tick={AX} width={84} />
        <Tooltip {...TT((v) => v)} formatter={(v, n, p) => [`${usd(v)} USDT · ${p?.payload?.count} işlem · ${p?.payload?.wins} kârlı`, "Net"]} />
        <ReferenceLine x={0} stroke="#64748B" />
        <Bar dataKey="net" isAnimationActive={false} radius={[0, 3, 3, 0]} data-testid={testid}>{rows.map((d) => <Cell key={d.symbol} fill={d.net >= 0 ? "#10B981" : "#EF4444"} />)}</Bar>
      </ComposedChart>
    </ResponsiveContainer>
  );
}
